import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import React, { useState } from 'react';
import NotificationContent from './NotificationContent';
import * as styles from './NotificationPopOver.module.scss';
import IconButton from '../components/IconButton';
import StickyHeader from '../components/StickyHeader';
import SVGIcon from '../components/SVGIcon';
import Tabs, { Tab } from '../components/Tabs';
import Tooltip from '../components/Tooltip';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import checkAllIcon from '../icons/non-prefilled/16x16/checkAll.svg';
import { isFeatureEnabled } from '../utils/features';

const CLEAR_ALL_NOTIFICATIONS_MUTATION = gql(/** @complexity 4 */ `
  mutation clearAllNotifications {
    markNotifications(status: READ) {
      id
      status
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-fields
const UNREAD_NOTIFICATIONS_QUERY = gql(/** @complexity 4 */ `
  query NotificationPopOver {
    session: currentSession {
      organization {
        id
        unreadNotifications {
          edges {
            __typename
          }
        }
      }
    }
  }
`);

const NOTIFICATIONS_FRAGMENT = gql(`
  fragment NotificationPopOver_notifications on Notification {
    id
    # eslint-disable-next-line @graphql-eslint/require-selections
    ...NotificationContent_notifications
  }
`);

type NotificationsFragment = DocumentType<typeof NOTIFICATIONS_FRAGMENT>;

interface NotificationPopOverProps {
  unreadNotifications?: NotificationsFragment[];
  allNotifications?: NotificationsFragment[];
  unreadNotificationsTotalCount: number;
  fetchMoreUnreadNotifications?: (() => void) | null;
  fetchMoreAllNotifications?: (() => void) | null;
  onNavigate?: () => void;
}

const NotificationPopOver = ({
  unreadNotifications = [],
  allNotifications = [],
  fetchMoreUnreadNotifications = null,
  fetchMoreAllNotifications = null,
  unreadNotificationsTotalCount,
  onNavigate,
}: NotificationPopOverProps) => {
  const [mode, setMode] = useState<'unread' | 'all'>('unread');

  const [clear] = useMutation(CLEAR_ALL_NOTIFICATIONS_MUTATION, {
    update: (store) => {
      const data = store.readQuery({ query: UNREAD_NOTIFICATIONS_QUERY });
      if (!data) return;
      store.writeQuery({
        query: UNREAD_NOTIFICATIONS_QUERY,
        data: {
          ...data,
          session: {
            ...data.session,
            organization: {
              ...data.session.organization,
              unreadNotifications: {
                ...data.session.organization.unreadNotifications,
                edges: [],
              },
            },
          },
        },
      });
    },
  });

  const stickyHeader = (
    <StickyHeader>
      <Tabs
        small
        right={
          <Tooltip content="Mark all as read" asChild>
            <IconButton
              onClick={() => clear()}
              aria-label="Mark all as read"
              className={styles.markAllButton}
            >
              <SVGIcon size={12}>{checkAllIcon}</SVGIcon>
            </IconButton>
          </Tooltip>
        }
      >
        <Tab
          className={styles.tabHeader}
          activeClassName={styles.active}
          onClick={() => setMode('unread')}
          active={mode === 'unread'}
        >
          Unread
          <span className={styles.tabCount}>
            {unreadNotificationsTotalCount < 100
              ? unreadNotificationsTotalCount
              : '99+'}
          </span>
        </Tab>
        <Tab
          className={styles.tabHeader}
          activeClassName={styles.active}
          onClick={() => setMode('all')}
          active={mode === 'all'}
        >
          All notifications
        </Tab>
      </Tabs>
    </StickyHeader>
  );

  const isNotificationsV2 = isFeatureEnabled('notifications_v2');

  // To preserve a scroll position when switching between tabs
  // return a copy of the popover content for each tab and show
  // only the one that is active
  return (
    <>
      <div
        className={classNames(styles.container, {
          [styles.hidden]: mode !== 'unread',
          [styles.isNotificationsV2]: isNotificationsV2,
        })}
      >
        {stickyHeader}
        <NotificationContent
          notifications={unreadNotifications}
          fetchMore={fetchMoreUnreadNotifications}
          onNavigate={onNavigate}
        />
      </div>
      <div
        className={classNames(styles.container, {
          [styles.hidden]: mode !== 'all',
          [styles.isNotificationsV2]: isNotificationsV2,
        })}
      >
        {stickyHeader}
        <NotificationContent
          notifications={allNotifications}
          fetchMore={fetchMoreAllNotifications}
          onNavigate={onNavigate}
        />
      </div>
    </>
  );
};

export default NotificationPopOver;
